import React from 'react'
import { Link } from '@tanstack/react-router'
import { faSignOut } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useTranslation } from 'react-i18next'

import { useAuth } from '@/auth'
import { Button } from '@/components/Button'

import './Header.css'

export function Header() {
  const { isAdmin, isNutritionist, isAmbassador, dbContext, logout } = useAuth()
  const { t } = useTranslation()

  // Pages backed by the nutritionist database (recipes, ingredients, ...) 403
  // for users without a privileged role, so only offer them the pages they
  // can use.
  const canAccessDbManagement = isAdmin || isNutritionist || isAmbassador

  return (
    <header className="Header">
      <div className="container">
        <div className="Header-wrap">
          <div className="Header-main">
            <svg
              width="29"
              height="37"
              viewBox="0 0 29 37"
              fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <path
                d="M18.0267 9.42042C17.9258 9.42017 17.8271 9.4498 17.743 9.50558C17.6589 9.56136 17.5933 9.64079 17.5543 9.73385C17.5153 9.82691 17.5047 9.92943 17.5239 10.0285C17.5431 10.1275 17.5912 10.2187 17.6622 10.2904C18.7314 11.381 19.5449 12.6955 20.0442 14.1388C20.5434 15.5822 20.7158 17.1184 20.5489 18.6365C19.9077 10.2771 8.95159 6.3741 3.16493 12.3044C-3.71788 19.0436 1.63246 31.1197 9.59278 34.0224C9.6716 34.0514 9.75617 34.0614 9.83958 34.0515C9.92298 34.0416 10.0029 34.012 10.0727 33.9653C10.1425 33.9186 10.2002 33.856 10.2412 33.7827C10.2822 33.7094 10.3052 33.6274 10.3085 33.5435V29.8693C14.9192 38.3617 12.969 37.8509 16.0978 35.5362C16.9564 34.9123 17.884 34.3892 18.8621 33.9772C21.1901 32.9715 28.3336 29.1137 28.3336 19.7167C28.3339 18.3636 28.0675 17.0237 27.5495 15.7736C27.0316 14.5236 26.2722 13.3879 25.3149 12.4316C24.3576 11.4753 23.2212 10.7171 21.9706 10.2004C20.72 9.68372 19.3798 9.41867 18.0267 9.42042Z"
                fill="#EF4659"
              />
              <path
                d="M1.85062 10.599C7.01738 5.79403 12.7162 7.15622 15.7705 8.63814C15.8193 8.66249 15.8732 8.67468 15.9277 8.67367C15.9822 8.67266 16.0357 8.65848 16.0835 8.63235C16.1313 8.60621 16.1721 8.56889 16.2024 8.52357C16.2327 8.47825 16.2516 8.42628 16.2574 8.37209L16.9438 0.363875C16.9472 0.307742 16.9368 0.251636 16.9134 0.200494C16.89 0.149351 16.8544 0.104738 16.8097 0.0705777C16.7651 0.0364177 16.7127 0.0137583 16.6572 0.00459112C16.6017 -0.00457608 16.5448 3.00272e-05 16.4915 0.0180052C13.5011 0.691121 14.1822 3.059 14.5786 5.33908C13.4213 3.52725 11.6041 1.72075 8.9915 1.83249C2.07411 2.13313 1.30255 8.15658 1.25732 10.3701C1.25724 10.4386 1.27749 10.5056 1.31549 10.5625C1.35348 10.6195 1.40753 10.6639 1.47078 10.6901C1.53403 10.7163 1.60364 10.7231 1.67078 10.7098C1.73793 10.6964 1.79959 10.6634 1.84796 10.6149"
                fill="#EF4659"
              />
            </svg>

            <ul role="navigation">
              {canAccessDbManagement && (
                <li>
                  <Link to="/recipes">{t('menu.recipes')}</Link>
                </li>
              )}
              {canAccessDbManagement && (
                <li>
                  <Link to="/ingredients/displaynames">{t('menu.ingredients')}</Link>
                </li>
              )}

              {isNutritionist && (
                <>
                  <li>
                    <Link to="/foodgroups">{t('menu.foodGroups')}</Link>
                  </li>
                  <li>
                    <Link to="/food-restrictions">{t('menu.foodRestrictions')}</Link>
                  </li>
                </>
              )}
              {canAccessDbManagement && (
                <li>
                  <Link to="/meal-simulation">{t('menu.mealSimulation')}</Link>
                </li>
              )}
              <li>
                <Link to="/usage-statistics/engagement">{t('menu.usageStats')}</Link>
              </li>
              {isNutritionist && (
                <li>
                  <Link to="/testing">{t('menu.testing')}</Link>
                </li>
              )}
              {isAdmin && (
                <>
                  <li>
                    <Link to="/users">{t('menu.users')}</Link>
                  </li>
                  <li>
                    <Link to="/settings">{t('menu.settings')}</Link>
                  </li>
                </>
              )}
              {canAccessDbManagement && (
                <li>
                  <Link to="/utils">{t('menu.utils')}</Link>
                </li>
              )}
            </ul>
          </div>

          <div className="Header-info-and-logout">
            <div>
              <div>
                <span className="Header-database-label">{t('menu.country')}</span>{' '}
                <span className="Header-database-value">{dbContext}</span>
              </div>
            </div>
            <Button className="Header-logout" onPress={logout}>
              <FontAwesomeIcon icon={faSignOut} color="var(--gray-100)" />
              {t('menu.logout')}
            </Button>
          </div>
        </div>
      </div>
    </header>
  )
}
